import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Typography, Paper, TextField, Button, Stack, Chip,
  IconButton, CircularProgress, Alert, Snackbar, Divider,
  Avatar, Tooltip, alpha,
} from "@mui/material";
import {
  Send, PersonAdd, Email, Delete, Refresh,
  CheckCircle, HourglassEmpty, WarningAmber,
  AdminPanelSettings, School,
} from "@mui/icons-material";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  secondarySofter: "#F0F8F3",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
};

const API_BASE = "https://backend-global-1vq9.onrender.com/api/auth";

const STATUS_CONFIG = {
  active:  { label: "Active",  color: C.secondary, bg: C.secondarySoft, icon: <CheckCircle sx={{ fontSize: 13 }} /> },
  pending: { label: "Pending", color: C.warning,   bg: C.warningSoft,   icon: <HourglassEmpty sx={{ fontSize: 13 }} /> },
  expired: { label: "Expired", color: C.error,     bg: C.errorSoft,     icon: <WarningAmber sx={{ fontSize: 13 }} /> },
};

const STATUS_RANK = { pending: 0, expired: 1, active: 2 };

export default function InviteMentor() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [invites, setInvites] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  // ── Gate: only superusers ──
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (!user || user.role !== "superuser") {
      navigate("/login", { state: { notice: "Superuser access required" } });
    }
  }, [navigate]);

  const authHeader = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchInvites = async () => {
    try {
      setLoadingList(true);
      const res = await axios.get(`${API_BASE}/invite/list`, { headers: authHeader() });
      const sorted = [...(res.data?.data || [])].sort((a, b) => {
        const r = STATUS_RANK[a.status] - STATUS_RANK[b.status];
        if (r !== 0) return r;
        return new Date(b.invitedAt || 0).getTime() - new Date(a.invitedAt || 0).getTime();
      });
      setInvites(sorted);
    } catch (err) {
      showSnack(err.response?.data?.msg || "Failed to load invites", "error");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { fetchInvites(); }, []);

  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const handleSend = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      setSending(true);
      const res = await axios.post(
        `${API_BASE}/invite/mentor`,
        { email: email.trim(), fullName: fullName.trim() || undefined },
        { headers: authHeader() }
      );
      showSnack(res.data?.message || "Invitation sent", "success");
      setEmail("");
      setFullName("");
      fetchInvites();
    } catch (err) {
      showSnack(err.response?.data?.msg || "Failed to send invitation", "error");
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (inviteEmail, inviteName) => {
    try {
      const res = await axios.post(
        `${API_BASE}/invite/mentor`,
        { email: inviteEmail, fullName: inviteName !== "—" ? inviteName : undefined },
        { headers: authHeader() }
      );
      showSnack(res.data?.message || "Invitation re-sent", "success");
      fetchInvites();
    } catch (err) {
      showSnack(err.response?.data?.msg || "Failed to resend invitation", "error");
    }
  };

  const handleRevoke = async (id, inviteEmail) => {
    if (!window.confirm(`Revoke invitation for ${inviteEmail}?`)) return;
    try {
      await axios.delete(`${API_BASE}/invite/${id}`, { headers: authHeader() });
      showSnack("Invitation revoked", "success");
      fetchInvites();
    } catch (err) {
      showSnack(err.response?.data?.msg || "Failed to revoke", "error");
    }
  };

  const counts = {
    active:  invites.filter((i) => i.status === "active").length,
    pending: invites.filter((i) => i.status === "pending").length,
    expired: invites.filter((i) => i.status === "expired").length,
  };

  const StatTile = ({ label, value, accent, accentDark, icon }) => (
    <Paper
      elevation={0}
      sx={{
        position: "relative", flex: 1, minWidth: 0,
        p: { xs: 1.75, sm: 2 }, borderRadius: 2.5,
        background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
        color: "#fff", overflow: "hidden",
        "&::after": {
          content: '""', position: "absolute",
          top: "-30%", right: "-15%",
          width: 80, height: 80, borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5,
          bgcolor: "rgba(255,255,255,0.18)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>{icon}</Box>
        <Box minWidth={0}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.85)", textTransform: "uppercase", letterSpacing: 0.5 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 800, color: "#fff", lineHeight: 1 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 5, lg: 10 }, py: { xs: 3, md: 4 } }}>
        {/* ── Hero ─────────────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative", borderRadius: 3, overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 4 },
            mb: { xs: 2.5, sm: 3 },
            color: "#fff",
            boxShadow: `0 6px 24px ${C.primary}30`,
          }}
        >
          <Box sx={{
            position: "absolute", top: "-40%", right: "-10%",
            width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} mb={1.5}>
              <AdminPanelSettings sx={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }} />
              <Typography sx={{
                fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                textTransform: "uppercase", color: "rgba(255,255,255,0.75)",
              }}>
                Superuser Console
              </Typography>
            </Stack>
            <Typography sx={{
              fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
              color: "#fff", lineHeight: 1.15, mb: 0.75, letterSpacing: -0.5,
            }}>
              Invite Mentors
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: { xs: 13, sm: 14 }, maxWidth: 560, lineHeight: 1.5 }}>
              Send invitation emails to mentors. They'll complete their account setup from a secure link.
            </Typography>
          </Box>
        </Paper>

        {/* Stats */}
        {invites.length > 0 && (
          <Stack direction="row" spacing={{ xs: 1, sm: 2 }} mb={{ xs: 2.5, sm: 3 }}
            sx={{ flexWrap: { xs: "wrap", sm: "nowrap" }, gap: { xs: 1, sm: 0 } }}>
            <StatTile label="Active Mentors" value={counts.active}
              accent={C.secondary} accentDark={C.secondaryDark}
              icon={<School sx={{ fontSize: 16, color: "#fff" }} />} />
            <StatTile label="Pending" value={counts.pending}
              accent={C.warning} accentDark="#A55F00"
              icon={<HourglassEmpty sx={{ fontSize: 16, color: "#fff" }} />} />
            {counts.expired > 0 && (
              <StatTile label="Expired" value={counts.expired}
                accent={C.error} accentDark="#991B1B"
                icon={<WarningAmber sx={{ fontSize: 16, color: "#fff" }} />} />
            )}
          </Stack>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
          {/* ── Invite form ─────────────────────────────────────── */}
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              width: { xs: "100%", md: 360 }, flexShrink: 0,
              p: 3, borderRadius: 3,
              border: `1px solid ${C.border}`,
              bgcolor: C.paper,
              boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0, left: 0, right: 0,
                height: "3px",
                background: `linear-gradient(90deg, ${C.primary}, ${C.primary}88)`,
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25} mb={2}>
              <PersonAdd sx={{ fontSize: 20, color: C.primary }} />
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: C.textPrimary }}>
                Send Invitation
              </Typography>
            </Stack>
            <Divider sx={{ mb: 2.5, borderColor: C.borderSoft }} />

            <form onSubmit={handleSend}>
              <Stack spacing={2}>
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.textSecondary, mb: 0.75 }}>
                    Email *
                  </Typography>
                  <TextField
                    fullWidth size="small" type="email"
                    placeholder="mentor@example.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.5, fontSize: 13.5,
                        "&.Mui-focused fieldset": { borderColor: C.primary },
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.textSecondary, mb: 0.75 }}>
                    Full Name (optional)
                  </Typography>
                  <TextField
                    fullWidth size="small"
                    placeholder="Personalizes the email"
                    value={fullName} onChange={(e) => setFullName(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.5, fontSize: 13.5,
                        "&.Mui-focused fieldset": { borderColor: C.primary },
                      },
                    }}
                  />
                </Box>

                <Button
                  type="submit" fullWidth disabled={sending || !email.trim()}
                  startIcon={!sending && <Send sx={{ fontSize: 17 }} />}
                  sx={{
                    background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
                    color: "#fff",
                    borderRadius: 2.5,
                    py: 1.35, fontSize: 13.5, fontWeight: 700,
                    textTransform: "none", boxShadow: "none",
                    "&:hover": { boxShadow: `0 4px 14px ${C.primary}40` },
                    "&.Mui-disabled": { background: "#CCC", color: "#fff" },
                  }}
                >
                  {sending ? "Sending…" : "Send Invitation"}
                </Button>
              </Stack>
            </form>

            <Box sx={{
              mt: 2.5, p: 1.5, borderRadius: 2,
              bgcolor: C.primarySofter,
              border: `1px solid ${C.primarySoft}`,
            }}>
              <Typography sx={{ fontSize: 11.5, color: C.textSecondary, lineHeight: 1.5 }}>
                <strong style={{ color: C.primary }}>How it works:</strong> The mentor receives an email with a secure link. They set their password and complete their profile. The invite expires in 7 days.
              </Typography>
            </Box>
          </Paper>

          {/* ── Invites list ────────────────────────────────────── */}
          <Paper
            elevation={0}
            sx={{
              flex: 1, width: "100%", p: 3, borderRadius: 3,
              border: `1px solid ${C.border}`,
              bgcolor: C.paper,
              boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
              position: "relative", overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0, left: 0, right: 0,
                height: "3px",
                background: `linear-gradient(90deg, ${C.secondary}, ${C.secondary}88)`,
              },
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Email sx={{ fontSize: 20, color: C.secondary }} />
                <Typography sx={{ fontWeight: 700, fontSize: 16, color: C.textPrimary }}>
                  Mentors & Invitations
                </Typography>
                <Chip
                  label={invites.length}
                  size="small"
                  sx={{
                    bgcolor: C.primarySoft, color: C.primary,
                    fontWeight: 700, height: 22,
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              </Stack>
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={fetchInvites} sx={{ color: C.primary, "&:hover": { bgcolor: C.primarySoft } }}>
                  <Refresh sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
            <Divider sx={{ mb: 2, borderColor: C.borderSoft }} />

            {loadingList ? (
              <Stack alignItems="center" py={4}>
                <CircularProgress size={32} sx={{ color: C.primary }} />
              </Stack>
            ) : invites.length === 0 ? (
              <Stack alignItems="center" spacing={1} py={4}>
                <Email sx={{ fontSize: 44, color: C.textMuted, opacity: 0.5 }} />
                <Typography sx={{ fontWeight: 700, color: C.textPrimary, fontSize: 14 }}>
                  No invitations yet
                </Typography>
                <Typography sx={{ fontSize: 12, color: C.textSecondary }}>
                  Send your first mentor invitation using the form
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.25}>
                {invites.map((inv) => {
                  const sc = STATUS_CONFIG[inv.status];
                  return (
                    <Box
                      key={inv._id}
                      sx={{
                        p: 1.75, borderRadius: 2.5,
                        border: `1px solid ${C.borderSoft}`,
                        bgcolor: inv.status === "active" ? C.secondarySofter : C.paper,
                        display: "flex", alignItems: "center", gap: 1.5,
                        flexWrap: "wrap",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: sc.color,
                          boxShadow: `0 4px 12px ${sc.color}15`,
                        },
                      }}
                    >
                      <Avatar sx={{
                        width: 40, height: 40,
                        background: inv.status === "active"
                          ? `linear-gradient(135deg, ${C.secondary}, ${C.secondaryLight})`
                          : `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                        color: "#fff", fontWeight: 700, fontSize: 16,
                      }}>
                        {(inv.fullName === "—" ? inv.email : inv.fullName).charAt(0).toUpperCase()}
                      </Avatar>

                      <Box flex={1} minWidth={0}>
                        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: C.textPrimary }}>
                            {inv.fullName === "—" ? "Pending mentor" : inv.fullName}
                          </Typography>
                          <Chip
                            icon={sc.icon}
                            label={sc.label}
                            size="small"
                            sx={{
                              height: 20, fontSize: 10.5, fontWeight: 700,
                              bgcolor: sc.bg, color: sc.color,
                              "& .MuiChip-label": { px: 0.5 },
                              "& .MuiChip-icon": { color: sc.color, ml: 0.5 },
                            }}
                          />
                        </Stack>
                        <Typography sx={{ fontSize: 12, color: C.textSecondary, mt: 0.25, wordBreak: "break-all" }}>
                          {inv.email}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={0.5} flexShrink={0}>
                        {inv.status !== "active" && (
                          <Tooltip title="Resend invitation">
                            <IconButton
                              size="small"
                              onClick={() => handleResend(inv.email, inv.fullName)}
                              sx={{ color: C.primary, "&:hover": { bgcolor: C.primarySoft } }}
                            >
                              <Refresh sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {inv.status !== "active" && (
                          <Tooltip title="Revoke invitation">
                            <IconButton
                              size="small"
                              onClick={() => handleRevoke(inv._id, inv.email)}
                              sx={{ color: C.error, "&:hover": { bgcolor: C.errorSoft } }}
                            >
                              <Delete sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Stack>
      </Box>

      <Snackbar
        open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 2.5, fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </Box>
  );
}